<template>
  <sui-modal v-model="visible" :closable="false">
    <sui-modal-header>
      Execute commands
    </sui-modal-header>
    <sui-modal-content>
      <sui-form :error="hasErrors">
        <sui-form-fields>
          <sui-form-field required :width="8">
            <label>Service</label>
            <sui-dropdown selection search v-model="serviceId" :options="servicesList" placeholder="Select service..." />
          </sui-form-field>
          <sui-form-field :width="8">
            <label>Container</label>
            <sui-dropdown selection search v-model="containerId" :options="containersList" placeholder="Execute on all service containers" />
          </sui-form-field>
        </sui-form-fields>
        <sui-form-field required>
          <label>Commands</label>
          <codemirror ref="commands" class="commands" v-model="commands" :options="options" v-if="entity" @ready="onCmReady" />
        </sui-form-field>
        <sui-form-field :error="hasUploadError">
          <label class="ui tiny button upload" :class="{loading: uploading}">
            <input type="file" ref="file" @change="upload()"/>
            <i class="upload icon" />
            Attach {{fileName ? 'other ' : ''}}file
          </label>
          <small v-if="fileName">
            Attached file: <b>{{fileName}}</b> <i class="times link icon" @click="removeFile()" />
          </small>
          <small class="errors">
            {{uploadError}}
          </small>
        </sui-form-field>
        <sui-message error v-if="apiError">
          {{apiError}}
        </sui-message>
      </sui-form>
    </sui-modal-content>
    <sui-modal-actions>
      <sui-button-group>
        <sui-button icon="check" label-position="left" color="green" :disabled="loading || hasErrors || !actions.serviceTerminal" @click="submit()">Execute</sui-button>
        <sui-button icon="ban" label-position="right" color="red" @click="cancel()">Cancel</sui-button>
      </sui-button-group>
    </sui-modal-actions>
    <sui-dimmer class="dimmer" :active="success" inverted>
      <h2 class="ui icon header">
        <i class="green check circle icon" />
        Executing started
      </h2>
    </sui-dimmer>
  </sui-modal>

</template>

<script>
  import {mapGetters} from 'vuex'
  import api from '@/api'
  import utils from '@/utils'

  export default {
    name: 'FormExecuteCommands',
    data() {
      return {
        apiError: null,
        commands: '',
        entity: null,
        fileUrl: null,
        fileName: '',
        loading: false,
        options: {
          lineNumbers: true,
          mode: 'shell',
        },
        serviceId: null,
        success: false,
        containerId: null,
        uploadError: null,
        uploading: false,
        visible: false,
      }
    },
    computed: {
      ...mapGetters(['actionsById', 'config', 'services', 'servicesById', 'containersById']),
      actions() {
        return this.actionsById[this.entity.service] || this.actionsById[this.entity.id]
      },
      servicesList() {
        return this.services.map(service => {
          const serviceLogo = service && service._logo
          return {
            image: serviceLogo ? {src: serviceLogo} : null,
            text: service.name,
            value: service.id,
            disabled: !this.actionsById[service.id].serviceTerminal,
          }
        })
      },
      containersList() {
        const containers = this.serviceId ? this.servicesById[this.serviceId]._containers : []
        const containersList = containers.map(container => {
          const host = container._host
          return {
            image: host ? {src: host._logo} : null,
            icon: host ? null : 'server',
            text: container.name,
            value: container.id,
            disabled: !this.actionsById[container._service.id],
          }
        })
        containersList.unshift({
          text: 'All service containers',
          value: null,
        })
        return containersList
      },
      hasApiError() {
        return Boolean(this.apiError)
      },
      hasUploadError() {
        return Boolean(this.uploadError)
      },
      hasErrors() {
        return !this.serviceId || !this.commands || this.hasApiError || this.hasUploadError
      },
    },
    watch: {
      visible() {
        if (this.visible) {
          if (this.entity) {
            if (this.entity.target === 'service') {
              this.serviceId = this.entity.id
            }
            if (this.entity.target === 'container') {
              this.serviceId = this.entity._service.id
              this.containerId = this.entity.id
            }
          }
          this.$nextTick(() => {
            this.$refs.commands.codemirror.focus()
          })
        } else {
          this.reset()
        }
      },
      serviceId() {
        this.containerId = null
      },
    },
    methods: {
      open(entity = null) {
        this.entity = entity
        this.visible = true
      },
      close() {
        this.visible = false
      },
      reset() {
        this.apiError = null
        this.commands = ''
        this.containerId = null
        this.entity = null
        this.fileName = ''
        this.fileUrl = null
        this.loading = false
        this.serviceId = null
        this.success = false
        this.uploadError = null
        this.uploading = false
      },
      onCmReady(codemirror) {
        setTimeout(() => {
          codemirror.refresh()
        }, 500)
      },
      upload() {
        const file = this.$refs.file.files[0]
        if (!file) {
          this.uploadError = 'File is not selected'
          return
        }
        this.fileName = file.name
        const uploadSize = this.config.uploadSize
        const goodSize = file.size < uploadSize
        if (!goodSize) {
          this.uploadError = 'Maximum file size: ' + utils.prettyBytes(uploadSize)
          return
        }
        if (file && goodSize) {
          this.fileUrl = ''
          this.uploading = true
          utils.reachGoal('EXECUTION_MANAGER_UPLOAD')
          api.upload(file)
            .finally(() => {
              this.uploading = false
              this.$refs.file.value = ''
            })
            .then(response => {
              this.fileUrl = response.data.result
            })
            .catch(error => {
              this.uploadError = error
            })
        }
      },
      removeFile() {
        this.fileUrl = ''
        this.fileName = ''
        this.uploadError = null
      },
      submit() {
        const entity = this.containersById[this.containerId] || this.servicesById[this.serviceId]
        const data = {execCommands: this.commands}
        if (this.fileUrl) {
          data.execFile = this.fileUrl
        }
        this.success = false
        this.loading = true
        this.apiError = null
        utils.reachGoal(`EXECUTION_MANAGER_EXECUTE_ON_${entity.target.toUpperCase()}`)
        entity.execCommands(data).finally(() => {
          this.loading = false
        }).then(() => {
          utils.reachGoal('EXECUTION_MANAGER_EXECUTE_SUCCESS')
          this.success = true
          setTimeout(() => {
            this.close()
          }, 1000)
        }).catch(error => {
          this.apiError = error
          utils.reachGoal('EXECUTION_MANAGER_EXECUTE_FAIL')
        })
      },
      cancel() {
        this.close()
      },
    },
    created() {
      this.$modals.executeCommands = this
    },
  }
</script>

<style scoped>
  .errors { display: none; color: #9f3a38; }
  .error > .errors { display: block; }
  .times:hover { color: #900; }
</style>
